import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { PanelContent, PanelPortal } from '@frontend/panel-engine';
import { useBreakpoint } from '@frontend/responsiveness';
import { SurfaceHeader } from '@frontend/surface-header';
import { theme } from '@frontend/theme';
import { ModalControlModalProps, Tray } from '@frontend/design-system';
import { VerifyInsuranceForm } from './verify-insurance-form';

interface Props extends ModalControlModalProps {
  hasVerificationError: boolean;
  shouldShowTuuthFairyReport: boolean;
  onReportLearnMoreClick: () => void;
  closeInsuranceSidePanel: () => void;
  eligibilityListAppointmentDate?: string;
}

type VerifyInsuranceSidePanelProps = Omit<Props, 'closeInsuranceSidePanel'> & {
  // Set this true for a scenario where only the modal should be used instead of PanelEngine
  useModal?: boolean;
};

const VerifyInsurancePanelEngineDesktopView = ({
  onReportLearnMoreClick,
  closeInsuranceSidePanel,
  hasVerificationError,
  shouldShowTuuthFairyReport,
  eligibilityListAppointmentDate,
}: Props) => {
  const { t } = useTranslation('insuranceDetails');

  return (
    <PanelPortal id='weave-insurance-verification' width='smallXL' onClose={closeInsuranceSidePanel} enableAnimations>
      <SurfaceHeader.Root>
        <SurfaceHeader.Row>
          <SurfaceHeader.Title>{t('Verify Insurance')}</SurfaceHeader.Title>
          <SurfaceHeader.Actions>
            <SurfaceHeader.Icon iconName='x' label={t('Close')} onClick={closeInsuranceSidePanel} />
          </SurfaceHeader.Actions>
        </SurfaceHeader.Row>
      </SurfaceHeader.Root>
      <PanelContent css={panelContentCss}>
        <VerifyInsuranceForm
          hasVerificationError={hasVerificationError}
          onClose={closeInsuranceSidePanel}
          onReportLearnMoreClick={onReportLearnMoreClick}
          shouldShowTuuthFairyReport={shouldShowTuuthFairyReport}
          eligibilityListAppointmentDate={eligibilityListAppointmentDate}
        />
      </PanelContent>
    </PanelPortal>
  );
};
const VerifyInsuranceSideTrayMobileView = ({
  onReportLearnMoreClick,
  closeInsuranceSidePanel,
  shouldShowTuuthFairyReport,
  hasVerificationError,
  eligibilityListAppointmentDate,
  ...modalProps
}: Props) => {
  const { t } = useTranslation('insuranceDetails');
  return (
    <Tray {...modalProps} width='smallXL' css={{ padding: '0', gap: 0 }} disableCloseOnEscape>
      <SurfaceHeader.Root>
        <SurfaceHeader.Row>
          <SurfaceHeader.Title>{t('Verify Insurance')}</SurfaceHeader.Title>
          <SurfaceHeader.Actions>
            <SurfaceHeader.Icon iconName='x' label={t('Close')} onClick={closeInsuranceSidePanel} />
          </SurfaceHeader.Actions>
        </SurfaceHeader.Row>
      </SurfaceHeader.Root>
      <Tray.Body css={panelContentCss}>
        <VerifyInsuranceForm
          hasVerificationError={hasVerificationError}
          onClose={closeInsuranceSidePanel}
          onReportLearnMoreClick={onReportLearnMoreClick}
          shouldShowTuuthFairyReport={shouldShowTuuthFairyReport}
          eligibilityListAppointmentDate={eligibilityListAppointmentDate}
        />
      </Tray.Body>
    </Tray>
  );
};

export const VerifyInsuranceSidePanel = ({
  shouldShowTuuthFairyReport,
  useModal = false,
  onReportLearnMoreClick,
  ...modalProps
}: VerifyInsuranceSidePanelProps) => {
  const size = useBreakpoint();
  const isDesktop = size === 'large' || size === 'xlarge';

  /**
   * When openModal is triggered from useModalControl then modalProps.show is set to true and sidePanel is displayed.
   * When onClose is triggered from useModalControl then modalProps.show is set to false and sidepanel is not displayed.
   */
  if (!modalProps.show) {
    return null;
  }
  if (isDesktop && !useModal) {
    return (
      <VerifyInsurancePanelEngineDesktopView
        {...modalProps}
        shouldShowTuuthFairyReport={shouldShowTuuthFairyReport}
        onReportLearnMoreClick={onReportLearnMoreClick}
        closeInsuranceSidePanel={modalProps.onClose}
      />
    );
  }
  return (
    <VerifyInsuranceSideTrayMobileView
      {...modalProps}
      shouldShowTuuthFairyReport={shouldShowTuuthFairyReport}
      onReportLearnMoreClick={onReportLearnMoreClick}
      closeInsuranceSidePanel={modalProps.onClose}
    />
  );
};

const panelContentCss = css`
  padding: ${theme.spacing(3)};
  .tray-primary-button {
    margin-left: ${theme.spacing(2)};
  }
`;
